/**
 * /well-testing/ai — the AI Drafts register (WT-6, fieldiq-api#1261). PF-6's
 * register, for this vertical.
 *
 * A full-width register of the company's stored AI runs (GET /ai/digests,
 * newest first, the API's latest 20), filtered by kind and by job in the URL
 * so a draft's back arrow returns to the list as it was left. A row opens the
 * draft on its own page, where it is approved (optionally edited) or
 * dismissed.
 *
 * The band runs the compliance digest (as of the tenant's today); the panel
 * below drafts one job's shift summary (WtShiftSummaryLauncher, shared with
 * the job page). Both open the new draft. A pending
 * draft reads "Awaiting your approval" — nothing is published until a person
 * approves it. Admin, office staff and supervisor only (WT_AI_ROLES); anyone
 * else gets a plain sentence and no request is made.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardCheck, FileClock, Loader2, Sparkles } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { useAuth } from '../../../contexts/AuthContext';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { useRunWtAi, useWtAiDigests } from '../../../hooks/useWtAi';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { formatInstant } from '../../../lib/calendar-date';
import { queryLoadFailure } from '../../../lib/load-failure';
import { wtDay } from '../../../lib/well-testing';
import { WT_AI_AGENT_LABEL, canUseWtAi, wtAiAgentLabel, wtAiOutcomeSkin, wtAiRefusal, wtAiScopeText } from '../../../lib/wt-ai';
import { WT_AI_AGENTS, type WtAiStoredRun } from '../../../types/wt-ai';
import { useOpenRecord } from '../../downhole/recordPage';
import { WtJobSelect, WtShiftSummaryLauncher } from './WtAiBits';
import {
  WT_BAND_SECONDARY,
  WT_EYEBROW,
  WtBadge,
  WtChip,
  WtChipRow,
  WtEmpty,
  WtField,
  WtFormError,
  WtPanel,
  WtRegister,
  type WtColumn,
} from '../WtBits';

type Row = WtAiStoredRun & { id: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function WtAiDraftsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const allowed = canUseWtAi(user?.role);
  const tz = useTenantTimeZone();
  const [params, setParams] = useSearchParams();
  const agentParam = params.get('agent') ?? '';
  const agent = (WT_AI_AGENTS as readonly string[]).includes(agentParam) ? agentParam : '';
  const jobParam = params.get('jobId') ?? '';
  const jobId = UUID.test(jobParam) ? jobParam : '';
  const listQuery = useWtAiDigests({ agent: agent || undefined, jobId: jobId || undefined }, allowed);
  const allQuery = useWtAiDigests({}, allowed);
  const run = useRunWtAi();
  const [runError, setRunError] = useState<string | null>(null);

  const rows: Row[] = (listQuery.data ?? []).map((d) => ({ ...d, id: d.agentRunId }));
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data;
  const pendingCount = all ? all.filter((d) => d.outcome === 'pending').length : undefined;

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const opened = (id: string | undefined) => {
    if (id) openRecord(`/well-testing/ai/${id}`);
  };

  const runCompliance = () => {
    setRunError(null);
    run.mutate({ agent: 'wt_compliance_digest' }, {
      onSuccess: (r) => opened(r?.agentRunId),
      onError: (err) => setRunError(wtAiRefusal(err, 'The compliance digest did not run.')),
    });
  };

  const columns: WtColumn<Row>[] = [
    { key: 'kind', header: 'Draft', render: (d) => <span className="font-extrabold text-white">{wtAiAgentLabel(d.agent)}</span> },
    { key: 'covers', header: 'Covers', render: (d) => wtAiScopeText(String(d.agent), d.triggerData, wtDay) },
    { key: 'run', header: 'Run', render: (d) => formatInstant(d.createdAt, tz) ?? '—' },
    { key: 'draft', header: 'AI text', render: (d) => (d.draft ? 'Drafted' : 'Unavailable') },
    { key: 'status', header: 'Review', render: (d) => <WtBadge skin={wtAiOutcomeSkin(d.outcome)} value={d.outcome} /> },
  ];

  const actions = allowed ? (
    <div className="flex flex-wrap gap-2" data-testid="wt-ai-run-actions">
      <button type="button" className={WT_BAND_SECONDARY} onClick={runCompliance} disabled={run.isPending} data-testid="wt-ai-run-compliance">
        {run.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <ClipboardCheck className="size-4" aria-hidden="true" />}
        {run.isPending ? 'Running…' : 'Run compliance digest'}
      </button>
    </div>
  ) : undefined;

  const emptyText = agent
    ? `No ${WT_AI_AGENT_LABEL[agent as keyof typeof WT_AI_AGENT_LABEL].toLowerCase()} has been run${jobId ? ' for this job' : ''}.`
    : jobId
      ? 'No AI draft has been run for this job.'
      : 'No AI draft has been run yet. Draft a shift summary below, or run the compliance digest above.';

  return (
    <div data-testid="wt-ai-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WT_EYEBROW}
        title="AI Drafts"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={actions}
        stats={
          allowed
            ? [
                { icon: <FileClock className="size-4" aria-hidden="true" />, label: 'To review', sub: 'Awaiting approval', tone: 'accent', loading: allQuery.isLoading, value: displayValue(pendingCount) },
                { icon: <Sparkles className="size-4" aria-hidden="true" />, label: 'Drafts', sub: 'Latest 20', tone: 'neutral', loading: allQuery.isLoading, value: displayValue(all ? all.length : undefined) },
              ]
            : undefined
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!allowed ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="wt-ai-not-allowed">
              AI drafts are for admins, office staff and supervisors.
            </p>
          ) : (
            <>
              <p className="text-xs text-fieldiq-muted">
                Figures are computed exactly. The AI writes a draft explanation that waits for a person to approve or dismiss it — nothing is sent or published by the AI.
              </p>
              {runError && <WtFormError message={runError} />}
              <WtChipRow label="Draft kind">
                <WtChip label="All" active={!agent} onClick={() => setParam('agent', '')} />
                {WT_AI_AGENTS.map((a) => (
                  <WtChip key={a} label={WT_AI_AGENT_LABEL[a]} active={agent === a} onClick={() => setParam('agent', a)} />
                ))}
              </WtChipRow>
              <div className="w-full sm:w-72">
                <WtField label="Job">
                  <WtJobSelect value={jobId} onChange={(v) => setParam('jobId', v)} label="Job" testId="wt-ai-job-filter" allLabel="All jobs" />
                </WtField>
              </div>
              <WtPanel eyebrow="AI drafts" title="Drafts" testId="wt-ai-register">
                <WtRegister
                  query={listQuery}
                  rows={rows}
                  columns={columns}
                  rail={(d) => wtAiOutcomeSkin(d.outcome).rail}
                  onOpen={(d) => opened(d.agentRunId)}
                  subject="AI drafts"
                  testId="wt-ai-run"
                  minWidth="min-w-[760px]"
                  empty={<WtEmpty>{emptyText}</WtEmpty>}
                  card={(d) => (
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-extrabold text-white">{wtAiAgentLabel(d.agent)}</p>
                        <p className="mt-0.5 text-xs text-fieldiq-muted">{wtAiScopeText(String(d.agent), d.triggerData, wtDay)}</p>
                        <p className="mt-0.5 text-xs text-fieldiq-muted">{formatInstant(d.createdAt, tz) ?? '—'}</p>
                      </div>
                      <WtBadge skin={wtAiOutcomeSkin(d.outcome)} value={d.outcome} />
                    </div>
                  )}
                />
              </WtPanel>
              <WtShiftSummaryLauncher onRan={(r) => opened(r?.agentRunId)} />
            </>
          )}
        </div>
      </section>
    </div>
  );
}
